// The cart's RBP missed-products widget data. rbp's candidates for the active ordering entry are
// read once per entry and user and cached for `MISSED_PRODUCTS_CACHE_TTL_MS` (the core marks'
// discipline: a cart left open reads them again on the half-TTL tick, since the nightly RBP load
// can move a store's report). Nothing is shown or priced until the cart is known (its basket read
// and its review both in), so a cart still loading never reads as empty. The candidates are then
// narrowed against the cart on every render, priced through the shared price store only as far as
// the cap needs (about ten per department, more as earlier ones drop), and capped per department. A product the
// Shopper adds leaves the list on the next render, because the cart then holds it (SL1.0 reloads
// the cart, `AddProductAndReloadCart`).
//
// The read is skipped where the server would answer nothing anyway: a catalogue that shows no RBP
// feature, a role outside the browse-side roles, or a licence-trade session. The server decides;
// this only saves the request. A failed read or price leaves the widget empty, never a toast; a
// failed price is asked again on the next change of the cart.

import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { fetchMissedProducts, type MissedProduct } from '../api/rbp-missed-products.client';
import { isRbpEntryKind } from '../api/rbp.client';
import { RBP_BROWSE_SIDE_ROLES } from '../auth/rbac';
import { useSessionStore } from '../auth/useSessionStore';
import { CORE_MARKS_CACHE_TTL_MS } from '../basket/useCoreMarks';
import { provisionalUnitPrice } from '../pricing/provisionalPrice';
import { realPricesEnabled } from '../pricing/realPrices';
import { rbpMissedProductsEnabled } from './rbpMissedProductsFlag';
import {
  useCurrentPriceKey,
  usePriceAccountCode,
  usePriceStore,
  usePrices,
} from '../pricing/usePriceStore';
import type { MoneyValue } from '../shared/money';
import {
  candidatesForCart,
  narrowMissedProducts,
  skusToPrice,
  type MissedProductGroup,
} from './missedProducts';

/** How long an answer is trusted: the core marks' figure. */
export const MISSED_PRODUCTS_CACHE_TTL_MS = CORE_MARKS_CACHE_TTL_MS;

interface MissedProductsState {
  /** The entry and user the answer belongs to: `${userKey}|${entryKey}`. */
  readonly scope: string | null;
  readonly candidates: readonly MissedProduct[] | null;
  load: (scope: string, entryKey: string) => Promise<void>;
  reset: () => void;
}

let inFlight: string | null = null;
let answeredAt: number | null = null;

export const useMissedProductsStore = create<MissedProductsState>((set, get) => ({
  scope: null,
  candidates: null,
  async load(scope, entryKey) {
    if (get().scope !== scope) {
      inFlight = null;
      answeredAt = null;
      set({ scope, candidates: null });
    }
    const fresh = answeredAt !== null && Date.now() - answeredAt < MISSED_PRODUCTS_CACHE_TTL_MS;
    if (fresh || inFlight === scope) return;
    inFlight = scope;
    const candidates = await fetchMissedProducts(entryKey);
    if (inFlight === scope) inFlight = null;
    // An answer for a scope we left is dropped; an unread one keeps the previous answer and is
    // asked again on the next tick.
    if (get().scope !== scope || candidates === null) return;
    answeredAt = Date.now();
    set({ candidates });
  },
  reset() {
    inFlight = null;
    answeredAt = null;
    set({ scope: null, candidates: null });
  },
}));

export interface CartFacts {
  /** The cart's basket and review have both loaded: until then nothing is shown or priced. */
  readonly cartKnown: boolean;
  /** The SKUs in the cart. */
  readonly cartSkus: readonly string[];
  /** The names of the departments the cart review groups its lines under. */
  readonly cartDepartments: readonly string[];
}

export interface MissedProducts {
  /** The departments to show, each with at most ten products; empty when there is nothing. */
  readonly groups: readonly MissedProductGroup[];
}

/** The ordering-entry kind of an entry key (`DEPOT:21` is `DEPOT`). */
function entryKindOf(entryKey: string): string {
  return entryKey.split(':')[0] ?? '';
}

export function useMissedProducts({
  cartKnown,
  cartSkus,
  cartDepartments,
}: CartFacts): MissedProducts {
  const userKey = useSessionStore((s) => s.identity?.sub ?? null);
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const role = useSessionStore((s) => s.role);
  const accountType = useSessionStore((s) => s.accountType);

  const shows =
    rbpMissedProductsEnabled() &&
    userKey !== null &&
    entryKey !== null &&
    isRbpEntryKind(entryKindOf(entryKey)) &&
    role !== null &&
    RBP_BROWSE_SIDE_ROLES.includes(role) &&
    accountType !== 'LicenceTrade';
  const scope = shows ? `${userKey}|${entryKey}` : null;

  useEffect(() => {
    if (scope === null || entryKey === null) return;
    const load = () => void useMissedProductsStore.getState().load(scope, entryKey);
    load();
    // Answers are stamped when the response lands, after this timer starts, so it ticks at half
    // the TTL: an answer is re-read by one and a half TTLs at most.
    const timer = setInterval(load, MISSED_PRODUCTS_CACHE_TTL_MS / 2);
    return () => clearInterval(timer);
  }, [scope, entryKey]);

  const candidates = useMissedProductsStore((s) => (s.scope === scope ? s.candidates : null));
  // SL1.0 keeps to the cart's departments only when the cart holds any (`get_cart.php:1027-1028`):
  // an empty cart, or one whose lines name no department, considers every department.
  const eligible =
    candidates === null || !cartKnown
      ? []
      : candidatesForCart(
          candidates,
          new Set(cartSkus),
          cartDepartments.length === 0 ? null : new Set(cartDepartments),
        );
  const real = realPricesEnabled();
  const priceKey = useCurrentPriceKey();
  const storeKey = usePriceStore((s) => s.key);
  const entries = usePriceStore((s) => s.entries);
  const priceOf = (sku: string): MoneyValue | null | 'failed' | undefined => {
    // With real prices off every product surface shows the provisional price; so does this.
    if (!real) return provisionalUnitPrice(sku);
    const entry = priceKey !== null && storeKey === priceKey ? entries[sku] : undefined;
    if (entry === undefined || entry.state === 'loading') return undefined;
    if (entry.state === 'failed') return 'failed';
    return entry.state === 'priced' ? entry.unitPrice : null;
  };
  usePrices(skusToPrice(eligible, priceOf));

  // A failed price read empties the widget (no guessed list) and is asked again once, on the next
  // change of the cart, never in a loop.
  const failedSkus = eligible
    .map((product) => product.productCode)
    .filter((sku) => priceOf(sku) === 'failed');
  const accountCode = usePriceAccountCode();
  const cartKey = [...new Set(cartSkus)].sort().join(',');
  const failedRef = useRef<readonly string[]>([]);
  failedRef.current = failedSkus;
  const lastCartKey = useRef(cartKey);
  useEffect(() => {
    if (lastCartKey.current === cartKey) return;
    lastCartKey.current = cartKey;
    if (failedRef.current.length === 0 || entryKey === null) return;
    void usePriceStore.getState().loadForSkus(entryKey, accountCode, failedRef.current);
  }, [cartKey, entryKey, accountCode]);

  if (eligible.length === 0 || failedSkus.length > 0) return { groups: [] };
  const prices = new Map<string, MoneyValue | null>();
  for (const { productCode } of eligible) {
    const price = priceOf(productCode);
    prices.set(productCode, price === undefined || price === 'failed' ? null : price);
  }
  return {
    groups: narrowMissedProducts({
      candidates: eligible,
      cartSkus: new Set(),
      cartDepartments: null,
      prices,
    }),
  };
}
